import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function EarlyAccessSection() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !role) {
      setError('Please fill in all fields.');
      return;
    }
    setError('');
    setLoading(true);

    const body = new URLSearchParams();
    body.append('email', email);
    body.append('role', role);

    try {
      await fetch('https://readdy.ai/api/form/d6ppeq3a90d3mf3eg5e0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="early-access" className="relative py-16 sm:py-24 overflow-hidden bg-[#060E1E]">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'linear-gradient(#00FFD1 1px, transparent 1px), linear-gradient(90deg, #00FFD1 1px, transparent 1px)', backgroundSize: '60px 60px' }}
      />

      {/* HUD corners */}
      <div className="absolute top-6 left-6 w-10 h-10 border-l-2 border-t-2 border-[#00FFD1]/40" />
      <div className="absolute top-6 right-6 w-10 h-10 border-r-2 border-t-2 border-[#00FFD1]/40" />
      <div className="absolute bottom-6 left-6 w-10 h-10 border-l-2 border-b-2 border-[#00FFD1]/40" />
      <div className="absolute bottom-6 right-6 w-10 h-10 border-r-2 border-b-2 border-[#00FFD1]/40" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-4 sm:px-5 py-2">
            <span className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse" />
            <span className="text-[#00FFD1] text-xs font-semibold uppercase tracking-widest">Limited Early Access</span>
          </div>
        </div>

        {/* Heading */}
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4 leading-tight">
          Get Early Access to<br />
          <span className="text-[#00FFD1]">WingmanFix</span>
        </h2>
        <p className="text-center text-gray-400 text-base sm:text-lg mb-8 sm:mb-10 max-w-xl mx-auto leading-relaxed">
          Be among the first pilots to fly with an AI copilot. Join the waitlist and we'll notify you the moment your spot opens.
        </p>

        {/* Perks row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 sm:mb-10">
          {[
            { icon: 'ri-shield-check-line', label: 'Free during beta' },
            { icon: 'ri-notification-3-line', label: 'First to know' },
            { icon: 'ri-gift-line', label: 'Founding member perks' },
          ].map((perk) => (
            <div key={perk.label} className="flex flex-col items-center gap-2 bg-white/5 border border-white/10 rounded-xl py-4 px-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${perk.icon} text-[#00FFD1] text-xl`} />
              </div>
              <span className="text-xs text-gray-300 text-center font-medium">{perk.label}</span>
            </div>
          ))}
        </div>

        {/* Form card */}
        <div className="bg-white/5 border border-[#00FFD1]/20 rounded-2xl p-6 sm:p-8 backdrop-blur-sm">
          {submitted ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-[#00FFD1]/10 rounded-full border border-[#00FFD1]/30">
                <i className="ri-check-line text-[#00FFD1] text-3xl" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2">You're on the list!</h3>
              <p className="text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">
                We'll reach out as soon as your early access spot is ready. Blue skies ahead.
              </p>
              <p className="mt-4 text-[#00FFD1] text-xs font-semibold uppercase tracking-widest">CLEARED FOR DEPARTURE ✓</p>
            </div>
          ) : (
            <form
              data-readdy-form
              id="early-access-waitlist"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="captain@example.com"
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#00FFD1]/60 focus:bg-white/8 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  I fly as a…
                </label>
                <select
                  name="role"
                  required
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full bg-[#0A1628] border border-white/20 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#00FFD1]/60 transition-all cursor-pointer appearance-none"
                >
                  <option value="" disabled>Select your role…</option>
                  <option value="Student Pilot">Student Pilot</option>
                  <option value="Private Pilot">Private Pilot (PPL)</option>
                  <option value="Instrument Rated">Instrument Rated (IFR)</option>
                  <option value="Commercial Pilot">Commercial Pilot (CPL)</option>
                  <option value="CFI / Flight Instructor">CFI / Flight Instructor</option>
                  <option value="ATP / Airline Pilot">ATP / Airline Pilot</option>
                  <option value="Aviation Enthusiast">Aviation Enthusiast</option>
                </select>
              </div>

              {error && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <i className="ri-error-warning-line" /> {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#00A3E0] hover:bg-[#0088BB] disabled:opacity-60 text-white font-bold py-4 rounded-lg transition-all hover:scale-[1.02] shadow-lg hover:shadow-[#00A3E0]/30 cursor-pointer whitespace-nowrap text-sm"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" /> Submitting…
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-line" /> Request Early Access
                  </span>
                )}
              </button>

              <p className="text-center text-xs text-gray-500">
                Want to share more about your flying?{' '}
                <Link to="/waitlist" className="text-[#00FFD1] hover:underline cursor-pointer">
                  Fill out the full sign-up form →
                </Link>
              </p>

              <p className="text-center text-xs text-gray-600">
                No spam. No credit card. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>

        {/* Social proof */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-xs sm:text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              {['bg-[#00A3E0]', 'bg-[#00FFD1]', 'bg-amber-400', 'bg-rose-400'].map((c, i) => (
                <div key={i} className={`w-7 h-7 rounded-full ${c} border-2 border-[#060E1E] flex items-center justify-center`}>
                  <i className="ri-user-line text-white text-xs" />
                </div>
              ))}
            </div>
            <span>340+ pilots already on the list</span>
          </div>
          <span className="hidden sm:block text-gray-700">·</span>
          <span className="flex items-center gap-1">
            <i className="ri-lock-line text-[#00FFD1]" /> Your data stays private
          </span>
        </div>
      </div>
    </section>
  );
}